// Enhanced error trap script
import { createLogger } from './logging';

function isAbortRejection(reason: unknown): boolean {
  const r: any = reason as any
  const name = typeof r?.name === 'string' ? r.name : ''
  const message = typeof r?.message === 'string' ? r.message : String(r ?? '')
  if (name === 'AbortError') return true
  if (message.includes('AbortError')) return true
  if (message.includes('signal is aborted')) return true
  return false
}

function describeReason(reason: unknown) {
  const r: any = reason as any
  return {
    name: typeof r?.name === 'string' ? r.name : undefined,
    message: typeof r?.message === 'string' ? r.message : typeof r === 'string' ? r : undefined,
    stack: typeof r?.stack === 'string' ? r.stack : undefined,
    cause: r?.cause,
    reason: r,
  }
}

export function setupErrorTraps() {
  const logger = createLogger();

  // Existing window.onerror handler
  const originalOnerror = window.onerror;
  window.onerror = (msg, url, lineNo, columnNo, error) => {
    // Ignore benign ResizeObserver errors
    if (typeof msg === 'string' && (msg.includes('ResizeObserver loop') || /Loop completed with undelivered notifications/.test(msg))) {
      return true;
    }
    if (error?.name === 'AbortError' || (typeof msg === 'string' && isAbortRejection(msg))) {
      logger.warning(`Global AbortError suppressed: ${String(msg)}`, { category: 'runtime', meta: { url, lineNo, columnNo } })
      return true
    }

    // Log to console and structured logger
    console.error('[Global Error]', { msg, url, lineNo, columnNo, error });
    logger.error(`Global Error: ${msg}`, {
      category: 'runtime',
      meta: { url, lineNo, columnNo, stack: error?.stack }
    });

    // Display error on screen
    displayErrorOverlay(`Runtime Error: ${msg}\n${url}:${lineNo}`);

    if (originalOnerror) return originalOnerror(msg, url, lineNo, columnNo, error);
    return false;
  };

  // Unhandled Promise Rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    // Ignore benign ResizeObserver errors inside promises
    if (event.reason?.message?.includes('ResizeObserver loop')) {
      return;
    }
    if (isAbortRejection(event.reason)) {
      const meta = describeReason(event.reason)
      console.warn('[Unhandled Rejection] AbortError suppressed', meta)
      logger.warning(`Unhandled Rejection (AbortError suppressed)`, { category: 'runtime', meta })
      return
    }

    console.error('[Unhandled Rejection]', event.reason);
    logger.error(`Unhandled Rejection: ${event.reason}`, {
      category: 'runtime',
      meta: describeReason(event.reason)
    });

    displayErrorOverlay(`Unhandled Promise Rejection: ${event.reason}`);
  });
}

function displayErrorOverlay(message: string) {
  const existing = document.getElementById('error-overlay');
  if (existing) {
    existing.innerHTML += `<br/><hr/><br/>${message}`;
    return;
  }

  const errorDiv = document.createElement('div');
  errorDiv.id = 'error-overlay';
  errorDiv.style.position = 'fixed';
  errorDiv.style.top = '0';
  errorDiv.style.left = '0';
  errorDiv.style.width = '100%';
  errorDiv.style.height = '100%'; // Cover full screen to be very visible
  errorDiv.style.padding = '20px';
  errorDiv.style.background = 'rgba(254, 226, 226, 0.95)'; // Red-50 with opacity
  errorDiv.style.color = '#991b1b'; // Red-800
  errorDiv.style.zIndex = '999999';
  errorDiv.style.borderBottom = '4px solid #b91c1c';
  errorDiv.style.overflow = 'auto';
  errorDiv.style.fontFamily = 'monospace';
  errorDiv.style.whiteSpace = 'pre-wrap';

  errorDiv.innerHTML = `<strong>CRITICAL ERROR DETECTED</strong><br/><br/>${message}`;

  // Add a dismiss button
  const dismissBtn = document.createElement('button');
  dismissBtn.innerText = 'Dismiss';
  dismissBtn.style.position = 'absolute';
  dismissBtn.style.top = '10px';
  dismissBtn.style.right = '10px';
  dismissBtn.style.padding = '5px 10px';
  dismissBtn.style.background = '#fff';
  dismissBtn.style.border = '1px solid #991b1b';
  dismissBtn.style.cursor = 'pointer';
  dismissBtn.onclick = () => errorDiv.remove();
  errorDiv.appendChild(dismissBtn);

  document.body.prepend(errorDiv);
}
